import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
  memo,
  useTransition,
  useRef,
} from 'react'
import { css } from '@emotion/css'
import { t } from '@lingui/core/macro'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DeleteIcon from '@mui/icons-material/Delete'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import EditIcon from '@mui/icons-material/Edit'
import ChevronUp from '@mui/icons-material/ExpandLess'
import ChevronDown from '@mui/icons-material/ExpandMore'
import ChevronLast from '@mui/icons-material/FirstPage'
import ChevronFirst from '@mui/icons-material/LastPage'
import RefreshIcon from '@mui/icons-material/Refresh'
import WarningIcon from '@mui/icons-material/Warning'
import { Typography } from '@mui/material'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Checkbox from '@mui/material/Checkbox'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grow from '@mui/material/Grow'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Popper from '@mui/material/Popper'
import SpeedDial from '@mui/material/SpeedDial'
import SpeedDialAction from '@mui/material/SpeedDialAction'
import SpeedDialIcon from '@mui/material/SpeedDialIcon'
import Tooltip from '@mui/material/Tooltip'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { CvsDrawer, Paper } from '@components'
import { SelectGroupedByFieldSet } from '@components/formFields/selectGroupedByFieldSet'
import { modalContext } from '@contexts'
import { colors as globalColors } from '@global/constants'
import { useCreativeClassifications } from '@global/hooks/queries/useCreativeClassifications'
import { EditTemplateField } from '../../../../field-governance/edit-template-field'
import { EditLink } from '../edit-link'
import * as TemplateActions from '../template.actions'
import { getIntegrationNameBySlug } from '../validateTemplate'

const selectFieldSetWrapper = css`
  display: flex;
  align-items: stretch;

  .clvn-v2-button__inner {
    white-space: pre-wrap;
    text-align: left;
  }
`

const getGovernanceTypes = () => ({
  constant: t`Constant`,
  autonumber: t`Auto number`,
  concatenation: t`Concatenation`,
  list: t`List`,
  cv_id: t`Claravine ID`,
  link: t`Link`,
  text: t`Text`,
  date: t`Date`,
  random: t`Random`,
  pattern: t`Pattern`,
  autofill: t`Autofill`,
  creative: t`Content Comprehension`,
  'shortened link': t`Shortened link`,
  'deep-link': t`Deep link`,
  'custom-bitlink': t`Custom Bitly link`,
})

function isVisible(item) {
  return !!item.visible_on_form || !!item.field?.visible_on_form
}

function visibilityIsToggable(item) {
  return !!item.default_value
}

function isRequired(item) {
  return !item.field || !!item.required || !!item.field.required
}

function isUnique(item) {
  return !item.field || !!item.must_be_unique || !!item.field.must_be_unique
}

function isRequirable(item) {
  if (item.governance_type === 'cv_id') {
    return true
  }
  if (item.governance_type === 'autonumber' || item?.field?.governance_type === 'autonumber') {
    return true
  }
  if (item.governance_type === 'random' || item?.field?.governance_type === 'random') {
    return true
  }
  if (item.governance_type === 'pattern') {
    return true
  }
  if (!item.field) {
    return true
  }
  if (item.governance_type === 'deep-link') {
    return true
  }
  if (item.governance_type === 'custom-bitlink') {
    return true
  }
  if (item.field?.governance_type === 'creative') {
    return true
  }
  return !!item.field.required
}

function isTextField(item) {
  return item?.field?.governance_type === 'text'
}

function isUniqueable(item) {
  return item?.field?.governance_type === 'text' || item?.field?.governance_type === 'concatenation'
}

function getUniqueTooltipText() {
  return t`The field must be unique for the entire account.`
}

function getRequiredTooltipText(item) {
  if (!item.field) {
    return t`The field powering this item has been deleted.`
  }
  if (item.field.required) {
    return t`This field is marked as required in its parent Field Set`
  }
  if (
    item.governance_type === 'pattern' ||
    item.governance_type === 'cv_id' ||
    item.governance_type === 'deep-link' ||
    item.governance_type === 'custom-bitlink'
  ) {
    return t`Claravine will always automatically generate this value`
  }
  return t`Control whether this field has to have a value before submission`
}

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}

export const FIELD_HEIGHT = 108

export const SchemaItem = memo(function SchemaItem(props) {
  const {
    id,
    index,
    reorderMode,
    issue,
    isMappedToIntegration,
    isCreativeClassificationEnabled,
    hasSourceUrl,
    isImagePreviewEnabled,
    isCreateClassificationFeatureEnabled,
    getSelectedFieldIds,
  } = props

  const item = useSelector(state => state.Template.templateFieldsById[id])
  const integrations = useSelector(state => state.Template.integrations)
  const [open, setOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [hidden] = useState(false)

  const fieldsets = useSelector(state => state.Template.fieldsets)

  const dispatch = useDispatch()
  const ModalContext = useContext(modalContext)
  const anchorRef = useRef(null)

  const fieldsById = useSelector(state => state.Template.fieldsById, shallowEqual)
  const fieldsetsById = useSelector(state => state.Template.fieldsetsById, shallowEqual)
  const colors = useSelector(state => state.Template.colorItemsByType, shallowEqual)

  const allFields = useSelector(state => state.Template.templateFields, shallowEqual)
  const totalFields = allFields?.length || 0

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const openItemId = searchParams.get('open')
  const [editField, setEditField] = useState(false)
  const [, startTransition] = useTransition()
  const { data: classifications = [] } = useCreativeClassifications()

  useEffect(() => {
    if (openItemId && openItemId === String(item?.field_id)) {
      /**
       * This is to prevent a racing condition where we set the state and then the component re-renders
       * causing an issue where the field disappears.
       */
      startTransition(() => {
        setEditField(true)
        setOpen(true)
      })
    }
  }, [openItemId, item?.field_id])

  /*
   * Prevent deletion of a field if:
   * - The field is the cvid
   * - The field is part of a pattern
   * - The field is a deep-link
   * - The field is a custom-bitlink
   * - The field is powering part of a powered list
   *   o In this case we want to provide feedback on what lists are still being powered
   *     so we don't get questions about being unable to delete fields. This probably doesn't
   *     need to be a SpeedDialAction, but it breaks if it's not so until we do away with the
   *     SpeedDial parent it's just a button with no onClick.
   *
   * @param {object} item This is a TemplateField when editing a template, a Field when creating a new template
   */
  function getDeleteButton(item) {
    if (
      item.governance_type === 'cv_id' ||
      item.field?.segment_parent_field_id ||
      item.governance_type === 'deep-link' ||
      item.governance_type === 'custom-bitlink'
    )
      return null
    if (item.list_columns?.length) {
      const tooltipMessage = []
      item.list_columns.map(({ list_name }) => tooltipMessage.push(list_name))
      return (
        <SpeedDialAction
          key="no-delete"
          icon={<DeleteForeverIcon />}
          FabProps={{ size: 'small' }}
          tooltipTitle={
            <div>
              {t`This field can't be deleted because it is currently powering columns on the following lists:`}
              <ul>
                {tooltipMessage.map((list, i) => (
                  <li key={i}>{list}</li>
                ))}
              </ul>
            </div>
          }
          tooltipPlacement="bottom"
        />
      )
    }

    if (isThisFieldPoweringMappings()) {
      return (
        <SpeedDialAction
          key="no-delete"
          icon={<DeleteForeverIcon />}
          FabProps={{ size: 'small' }}
          tooltipTitle={
            <div>
              {t`This field can't be deleted because it is currently powering mappings on an integration. To delete, remove or change the mappings on the following integrations using this field:`}
              {integrations &&
                getPoweredIntegrations().map((integration, index) => (
                  <div key={index}>-{getIntegrationNameBySlug(integration.slug)}</div>
                ))}
            </div>
          }
          tooltipPlacement="bottom"
        />
      )
    }

    if (isMappedToIntegration) {
      return (
        <SpeedDialAction
          key="no-delete"
          icon={<DeleteForeverIcon />}
          FabProps={{ size: 'small' }}
          tooltipTitle={
            <div>
              {t`This field can't be deleted as it is currently mapped to an integration on this template. Please update the associated field mapping and try again.`}
            </div>
          }
          tooltipPlacement="bottom"
        />
      )
    }

    return (
      <SpeedDialAction
        data-test-page-id={`${item?.field?.name}_delete_button`}
        key="delete"
        icon={<DeleteIcon />}
        FabProps={{ size: 'small' }}
        tooltipTitle={t`Delete`}
        tooltipPlacement="bottom"
        onClick={() => {
          removeTemplateField(item)
          setOpen(false)
        }}
      />
    )
  }

  function getCcType(item) {
    /**
     * We are searching for the field inside template.fieldsets and not directly in the item.field
     * because item.field doesn't have the classification object.
     */
    const fieldset = fieldsets.find(f => f.id === item.field.fieldset_id)
    const field = fieldset.fields.find(f => f.id === item.field_id)
    const categoryId = field?.classification?.category_id

    return classifications.find(c => c.id === categoryId)?.categoryText
  }

  function getGovernanceType(item) {
    const governanceTypes = getGovernanceTypes()

    if (!item.field) {
      return t`No Field`
    }
    if (item.governance_type === 'pattern') {
      return governanceTypes.pattern
    }
    if (item.governance_type === 'list' || item.field.governance_type === 'list') {
      return governanceTypes.list
    }
    if (item.field.segment_parent_field_id && governanceTypes[item.governance_type]) {
      return governanceTypes[item.governance_type]
    }
    if (!item.governance_type && !item.field.governance_type) {
      return <WarningIcon style={{ color: globalColors.red.main }} />
    }
    return governanceTypes[fieldsById[item.field_id].governance_type]
  }

  function getGovernanceTooltip(item) {
    const governanceTypes = getGovernanceTypes()

    if (!item.field) {
      return t`No Field`
    }
    if (item.governance_type === 'pattern') {
      return governanceTypes.pattern
    }
    if (item.governance_type === 'list' || item.field.governance_type === 'list') {
      const listGov = item.list_governance || item.field.list_governance
      return (
        <div>
          <div>
            {`${governanceTypes.list}: ${
              listGov.lists ? listGov.lists.name : t`list has been deleted`
            }`}
          </div>
          <div>{item.default_value ? `${t`Filter Value:`} ${item.default_value}` : null}</div>
        </div>
      )
    }
    if (item.governance_type === 'constant' || item.field.governance_type === 'constant') {
      const conGov = item.constant_value || item.field.constant_value
      return (
        <div>
          <div>{governanceTypes.constant}</div>
          <div>{conGov ? `${t`Constant Value:`} ${conGov}` : null}</div>
        </div>
      )
    }
    if (item.governance_type === 'autofill' || item.field.governance_type === 'autofill') {
      const autoGov = item.autofill || item.field.autofill
      return (
        <div>
          <div>{governanceTypes.autofill}</div>
          <div>
            {autoGov ? `${t`Autofill Field:`} ${fieldsById[autoGov.field_id]?.name}` : null}
          </div>
        </div>
      )
    }
    if (item.field.segment_parent_field_id && governanceTypes[item.governance_type]) {
      return governanceTypes[item.governance_type]
    }
    if (!item.governance_type && !item.field.governance_type) {
      return t`This is used in a pattern and requires a type`
    }
    return governanceTypes[fieldsById[item.field_id].governance_type]
  }

  function getTextFieldsetName(item) {
    if (!item.field) {
      return ''
    }

    if (item.field.segment_parent_field_id) {
      return fieldsById[item.field.segment_parent_field_id]?.name
    }

    if (fieldsetsById[fieldsById[item.field_id].fieldset_id]) {
      return fieldsetsById[fieldsById[item.field_id].fieldset_id].name
    }

    return ''
  }

  function getFieldsetName(item) {
    if (!item.field) {
      return ''
    }
    if (item.field.segment_parent_field_id) {
      return (
        <div
          className="text-xs px-[15px] py-[5px] rounded-full overflow-hidden inline-block max-w-full"
          style={{ backgroundColor: getBackgroundColor(item), color: getColor(item) }}
        >
          <span className="font-bold">{t`Pattern:`}</span>
          {fieldsById[item.field.segment_parent_field_id]?.name}
        </div>
      )
    }
    if (fieldsetsById[fieldsById[item.field_id].fieldset_id]) {
      return (
        <div
          className="text-xs px-[15px] py-[5px] rounded-full overflow-hidden inline-block max-w-full"
          style={{ backgroundColor: getBackgroundColor(item), color: getColor(item) }}
        >
          <span className="font-bold">{t`Field set:`}</span>
          {fieldsetsById[fieldsById[item.field_id].fieldset_id].name}
        </div>
      )
    }
    return ''
  }

  function getFieldsPoweringMappings() {
    return integrations
      ? integrations
          .map(field => Object.values(field.mapping_fields ? field.mapping_fields : {}))
          .flat()
      : []
  }

  const searchFieldInMappings = (mappings, fieldId) =>
    mappings.filter(({ cvFieldId }) => cvFieldId == fieldId)

  const getMappingsPoweredByThisField = () =>
    searchFieldInMappings(getFieldsPoweringMappings(), item.field_id)

  const isThisFieldPoweringMappings = () => getMappingsPoweredByThisField().length > 0

  const getPoweredIntegrations = () =>
    integrations.filter(
      ({ mapping_fields }) =>
        mapping_fields && searchFieldInMappings(mapping_fields, item.field_id).length > 0,
    )

  function getBackgroundColor(item) {
    if (item.governance_type === 'pattern') {
      return colors.patterns[item.field_id]?.backgroundColor
    }
    if (item.field.segment_parent_field_id) {
      return colors.patterns[item.field.segment_parent_field_id]?.backgroundColor
    }
    return colors.fieldsets[item.field.fieldset_id]?.backgroundColor
  }

  function getColor(item) {
    if (item.governance_type === 'pattern') {
      return colors.patterns[item.field_id]?.color
    }
    if (item.field.segment_parent_field_id) {
      return colors.patterns[item.field.segment_parent_field_id]?.color
    }
    return colors.fieldsets[item.field.fieldset_id]?.color
  }

  function removeTemplateField(item) {
    if (item.field && item.field.is_pattern) {
      ModalContext.displayModal({
        title: t`Are you sure you want to remove this pattern?`,
        description: t`Removing a pattern will remove all of its children fields and all effected fields from any parameters or output configurations. This change will not be finalized until you save the template.`,
        confirm: () => () => {
          removePattern(item)
        },
        cancel: () => () => {
          setOpen(false)
        },
      })
    } else {
      dispatch(TemplateActions.removeTemplateField(item))
      setOpen(false)
    }
  }

  function removePattern(item) {
    dispatch(TemplateActions.removeTemplateField(item))
    setOpen(false)
  }

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const jumpReorder = (item, newIndex) => {
    const reorderFields = reorder(allFields, index, newIndex)
    dispatch(TemplateActions.setFields(reorderFields))
    setMenuOpen(false)
  }

  const moveUp = () => {
    if (index) {
      const reorderFields = reorder(allFields, index, index - 1)
      dispatch(TemplateActions.setFields(reorderFields))
      setMenuOpen(false)
    }
  }

  const moveDown = () => {
    if (index < allFields.length) {
      const reorderFields = reorder(allFields, index, index + 1)
      dispatch(TemplateActions.setFields(reorderFields))
      setMenuOpen(false)
    }
  }

  const moveTop = () => {
    const reorderFields = reorder(allFields, index, 0)
    dispatch(TemplateActions.setFields(reorderFields))
    setMenuOpen(false)
  }

  const moveBottom = () => {
    const reorderFields = reorder(allFields, index, allFields.length)
    dispatch(TemplateActions.setFields(reorderFields))
    setMenuOpen(false)
  }

  const handleChange = (item, field, newVal) => {
    const newItem = { ...item }
    newItem[field] = newVal
    dispatch(TemplateActions.updateTemplateField(newItem))
  }

  const handleCreativeClassificationChange = (item, creativeConfiguration) => {
    const isSourceUrl = creativeConfiguration.classification_parameter_type === 'SOURCE_URL'

    const newItem = {
      ...item,
      ...(isSourceUrl && { is_preview_url: true }),
      ...creativeConfiguration,
    }

    dispatch(TemplateActions.updateTemplateField(newItem))
  }

  function getFieldName() {
    if (!fieldsById[item.field_id]) return
    // if (fieldsById[item.field_id].governance_type === 'constant') {
    //   return fieldsById[item.field_id].name
    // }
    return fieldsById[item.field_id].name
  }

  const updateTF = updatedTF => {
    let newItem = {
      ...item,
      id: item.id,
      parent: updatedTF.parent,
      apply_default_value_to_empty_values: updatedTF.apply_default_value_to_empty_values,
      visible_on_form: updatedTF.visible_on_form,
    }

    if (updatedTF.field || updatedTF?.segment_type == 'variable') {
      newItem = {
        ...item,
        ...updatedTF,
        id: item.id,
      }
      dispatch(TemplateActions.updateTemplateField(newItem))
      return
    }

    dispatch(TemplateActions.updateTemplateField(newItem))

    const promise = new Promise(resolve => {
      resolve('success')
    })
    return promise
  }

  const reloadPattern = () => {
    dispatch(TemplateActions.refreshPattern(item, issue.data))
  }

  const isFabDisabled = useMemo(
    () =>
      (item.field &&
        item.field.segment_parent_field_id &&
        item.field?.segment_type !== 'variable') ||
      item.governance_type === 'cv_id',
    [item],
  )

  const getEditableTemplateField = useMemo(() => {
    const editableTF = {
      ...item.field,
    }
    if (item.field && item.field?.segment_type === 'variable') {
      editableTF.invalidTypes = { date: true, concatenation: true, pattern: true, autonumber: true }
      editableTF.governance_type = item.governance_type
      editableTF.constant_value = item.constant_value
      editableTF.list_governance = item.list_governance
      editableTF.date = item.date
      editableTF.autofill = item.autofill
      editableTF.validation = item.validation
      editableTF.transformation = item.transformation
      editableTF.concatenation_governance = item.concatenation_governance
      editableTF.max_length = item.max_length
      editableTF.min_length = item.min_length
    }
    editableTF.disabledFields = {
      governance: !(item.field && item.field?.segment_type === 'variable'),
      name: !(item.field && item.field.governance_type === 'link'),
      type: !(item.field && item.field?.segment_type === 'variable'),
      description: !(item.field && item.field?.segment_type === 'variable'),
      required: true,
      must_be_unique: true,
    }

    if (editableTF.is_pattern) {
      editableTF.governance_type = 'pattern'
    }

    if (
      editableTF.governance_type === 'list' &&
      editableTF.list_governance?.lists?.type === 'drill-down'
    ) {
      editableTF.apply_default_value_to_empty_values = item.apply_default_value_to_empty_values
    }

    editableTF.template_field_id = item.id
    editableTF.parent = item.parent

    return editableTF
  }, [item])

  const showRequireUniqueness = useSelector(state => state.Auth.features['require-uniqueness'])
  const isCreativeField = item.field?.governance_type === 'creative'

  const onSelectAddField = useCallback(
    selectedField => {
      dispatch(TemplateActions.addFields([selectedField]))
    },
    [fieldsById],
  )

  const showSelectGroupedByFieldSet = item?.field && item?.governance_type !== 'cv_id'

  return (
    <Tooltip title={issue ? issue.message : ''} placement="bottom">
      <Paper
        elevation={1}
        id={`SCHEMA_ITEM_${item.id}`}
        sx={{
          height: `${FIELD_HEIGHT}px`,
          padding: '10px',
          margin: '10px 0',
          boxShadow: '0 3px 6px rgba(0, 0, 0, 0.1)',
          border: '1px solid var(--clvn-blue-200)',
          borderLeft: !issue || issue.type === 'WARNING' ? '' : '4px solid #FF0000',
          paddingLeft: !issue || issue.type === 'WARNING' ? '24px' : '16px',
          ...(isCreativeClassificationEnabled &&
            hasSourceUrl &&
            isCreativeField && {
              position: 'relative',
              backgroundColor: '#faf9ff',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                left: '4px',
                width: '4px',
                height: '90%',
                background: '#7f52ff',
                borderRadius: '10px',
              },
            }),
        }}
      >
        <div className="grid grid-cols-[minmax(180px,_1fr)_300px_140px_1fr_100px] gap-[15px] h-full items-center">
          <div className="block overflow-hidden whitespace-nowrap text-ellipsis">
            {fieldsById[item.field_id] && fieldsById[item.field_id].is_pattern ? (
              <span
                style={{
                  backgroundColor: getBackgroundColor(item),
                  color: getColor(item),
                  fontSize: 12,
                  padding: '4px 8px',
                  borderRadius: 50,
                }}
              >
                {getFieldName(fieldsById, item)}
              </span>
            ) : (
              getFieldName(fieldsById, item)
            )}
          </div>
          <div css={selectFieldSetWrapper}>
            {showSelectGroupedByFieldSet ? (
              <SelectGroupedByFieldSet
                fieldsetId={item?.field?.fieldset_id}
                name={getTextFieldsetName(item)}
                onSelectCallback={onSelectAddField}
                excludeFieldId={getSelectedFieldIds()}
                noOptions={getFieldsetName(item)}
              />
            ) : null}
          </div>
          {reorderMode ? (
            <div className="flex items-stretch relative">
              <div style={{ marginBottom: 6, marginTop: 2 }}>
                <Typography variant="caption">{t`Reorder`}</Typography>
              </div>
              <div>
                <ButtonGroup color="primary" aria-label="outlined primary button group">
                  <Button onClick={moveTop}>
                    <ChevronFirst style={{ transform: 'rotate(-90deg)' }} />
                  </Button>
                  <Button onClick={moveUp}>
                    <ChevronUp />
                  </Button>
                  <Button onClick={moveDown}>
                    <ChevronDown />
                  </Button>
                  <Button onClick={moveBottom}>
                    <ChevronLast style={{ transform: 'rotate(-90deg)' }} />
                  </Button>
                  <Button
                    ref={anchorRef}
                    color="primary"
                    size="small"
                    aria-controls={menuOpen ? 'split-button-menu' : undefined}
                    aria-expanded={menuOpen ? 'true' : undefined}
                    aria-label="select merge strategy"
                    aria-haspopup="menu"
                    onClick={() => {
                      setMenuOpen(true)
                    }}
                  >
                    <ArrowDropDownIcon />
                  </Button>
                </ButtonGroup>
                <Popper
                  open={menuOpen}
                  anchorEl={anchorRef.current}
                  role={undefined}
                  transition
                  disablePortal
                  style={{
                    zIndex: 10,
                    overflowY: 'scroll',
                    maxHeight: 250,
                    boxShadow:
                      '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.1',
                  }}
                >
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      style={{
                        transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                      }}
                    >
                      <div>
                        <ClickAwayListener
                          onClickAway={() => {
                            setMenuOpen(false)
                          }}
                        >
                          <div>
                            <Paper elevation={6}>
                              <MenuList id="split-button-menu">
                                {[...Array(totalFields).keys()].map((option, i) => (
                                  <MenuItem key={option} onClick={event => jumpReorder(event, i)}>
                                    {option + 1}
                                  </MenuItem>
                                ))}
                              </MenuList>
                            </Paper>
                          </div>
                        </ClickAwayListener>
                      </div>
                    </Grow>
                  )}
                </Popper>
              </div>
              <div style={{ position: 'absolute', top: 4, right: 4 }}>{props.index + 1}</div>
            </div>
          ) : (
            <>
              <div className="flex flex-col items-stretch">
                <Tooltip placement="bottom" title={getGovernanceTooltip(item)}>
                  <div>{getGovernanceType(item)}</div>
                </Tooltip>
                {isCreativeField && (
                  <div>
                    <div style={{ marginBottom: 1, marginTop: 2 }}>
                      <Typography variant="caption" sx={{ fontSize: '11px' }}>
                        {t`CC type`}
                      </Typography>
                    </div>
                    {getCcType(item)}
                  </div>
                )}
              </div>
              <div className="flex items-stretch gap-[10px]">
                <div>
                  <div>
                    <Tooltip
                      title={t`Control whether this field will show up in the submission grid`}
                      placement="right"
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isVisible(item)}
                            onChange={(e, newVal) => {
                              handleChange(item, 'visible_on_form', newVal)
                            }}
                            value={isVisible(item)}
                            style={{ padding: 2 }}
                            size="small"
                            disabled={visibilityIsToggable(item)}
                            color="secondary"
                          />
                        }
                        label={t`Visible`}
                        style={{ marginLeft: 0 }}
                      />
                    </Tooltip>
                  </div>
                  <div>
                    <Tooltip title={getRequiredTooltipText(item)} placement="right">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isRequired(item)}
                            onChange={(e, newVal) => {
                              handleChange(item, 'required', newVal)
                            }}
                            value={isRequired(item)}
                            style={{ padding: 2 }}
                            color="secondary"
                            size="small"
                            disabled={isRequirable(item)}
                          />
                        }
                        label={t`Required`}
                        style={{ marginLeft: 0 }}
                      />
                    </Tooltip>
                  </div>
                  <div>
                    {showRequireUniqueness && isUniqueable(item) ? (
                      <Tooltip title={getUniqueTooltipText(item)} placement="right">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isUnique(item)}
                              value={isUnique(item)}
                              style={{ padding: 2 }}
                              size="small"
                              color="secondary"
                              disabled
                            />
                          }
                          label={t`Require uniqueness`}
                          style={{ marginLeft: 0 }}
                        />
                      </Tooltip>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
                <div>
                  {isImagePreviewEnabled && isTextField(item) && (
                    <div>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(item.is_preview_url)}
                            onChange={(e, newVal) => {
                              handleChange(item, 'is_preview_url', newVal)
                            }}
                            value={Boolean(item.is_preview_url)}
                            style={{ padding: 2 }}
                            size="small"
                            color="secondary"
                          />
                        }
                        label={t`Enable asset preview`}
                        style={{ marginLeft: 0 }}
                      />
                    </div>
                  )}
                  {isCreateClassificationFeatureEnabled &&
                    isCreativeClassificationEnabled &&
                    isTextField(item) && (
                      <>
                        <div>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={item.classification_parameter_type === 'SOURCE_URL'}
                                onChange={(e, newVal) => {
                                  handleCreativeClassificationChange(item, {
                                    generates_creative_classification: newVal,
                                    classification_parameter_type: newVal ? 'SOURCE_URL' : null,
                                  })
                                }}
                                value={item.classification_parameter_type === 'SOURCE_URL'}
                                style={{ padding: 2 }}
                                size="small"
                                color="secondary"
                              />
                            }
                            label={t`AI classifications source URL`}
                            style={{ marginLeft: 0 }}
                          />
                        </div>
                        <div>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={item.classification_parameter_type === 'TITLE'}
                                onChange={(e, newVal) => {
                                  handleCreativeClassificationChange(item, {
                                    generates_creative_classification: newVal,
                                    classification_parameter_type: newVal ? 'TITLE' : null,
                                  })
                                }}
                                value={item.classification_parameter_type === 'TITLE'}
                                style={{ padding: 2 }}
                                size="small"
                                color="secondary"
                              />
                            }
                            label={t`AI classifications title`}
                            style={{ marginLeft: 0 }}
                          />
                        </div>
                      </>
                    )}
                </div>
              </div>
              <div className="flex items-stretch relative justify-self-center">
                <SpeedDial
                  data-test-page-id={`${item?.field?.name}_actions_button`}
                  ariaLabel="SpeedDial tooltip example"
                  hidden={hidden}
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                  icon={<SpeedDialIcon style={{ paddingTop: 1 }} />}
                  onClose={handleClose}
                  FabProps={{ size: 'small', color: 'secondary', disabled: isFabDisabled }}
                  onOpen={handleOpen}
                  open={open}
                  direction="left"
                >
                  <SpeedDialAction
                    data-test-page-id={`${item?.field?.name}_edit_button`}
                    key="edit"
                    icon={<EditIcon />}
                    FabProps={{ size: 'small' }}
                    tooltipPlacement="bottom"
                    tooltipTitle={t`Edit`}
                    onClick={() => {
                      setEditField(true)
                      setOpen(false)
                    }}
                  />
                  {getDeleteButton(item)}
                  {item.field &&
                  item.governance_type == 'pattern' &&
                  issue?.message.includes('has been updated') ? (
                    <SpeedDialAction
                      data-test-page-id={`${item?.field?.name}_refresh_button`}
                      key="refresh"
                      icon={<RefreshIcon />}
                      FabProps={{ size: 'small' }}
                      tooltipTitle={t`Reload pattern`}
                      tooltipPlacement="bottom"
                      onClick={() => {
                        reloadPattern(item)
                      }}
                    />
                  ) : null}
                </SpeedDial>
              </div>
            </>
          )}
        </div>
        {item.governance_type == 'link' || (item.field && item.field.governance_type === 'link') ? (
          <EditLink
            title={item ? `${t`Edit`} ${item.field.name}` : t`Edit`}
            fieldId={item?.id || ''}
            visible={Boolean(editField)}
            onClose={() => {
              setEditField(false)
              setOpen(false)
            }}
          />
        ) : (
          <CvsDrawer
            title={item ? `${t`Edit`} ${item.field && item.field.name}` : t`Edit`}
            visible={Boolean(editField)}
            onClose={() => {
              setEditField(false)
              setOpen(false)
            }}
          >
            {item.field && (
              <EditTemplateField
                close={() => {
                  setEditField(false)
                  setOpen(false)
                }}
                submit={updateTF}
                fieldSetId={item.field && item.field.fieldset_id}
                fieldId={item.field_id}
                field={getEditableTemplateField}
              />
            )}
          </CvsDrawer>
        )}
      </Paper>
    </Tooltip>
  )
})
